// admin/src/lib/slugify.ts
//
// Equipment ids are a slug auto-generated from the English name
// (supabase/migrations/0025_media_library.sql: `id text primary key
// constraint equipment_id_format check (id ~ '^[a-z0-9_]{2,40}$')`) --
// underscores only, no hyphens, 2-40 chars. Locked after create (the edit
// page never re-slugifies). Hebrew-only names (no Latin letters to
// transliterate) fall back to a short random id rather than producing an
// empty string, which would fail the length check.

const MAX_LEN = 40;
const MIN_LEN = 2;

/** True transliteration isn't attempted; Hebrew input has no Latin form here, so it is dropped and the fallback below kicks in. */
function stripToAsciiWords(input: string): string {
  return input
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '') // diacritics
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');
}

function randomSuffix(len = 6): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let out = '';
  for (let i = 0; i < len; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

/**
 * Slugify an equipment name_en into an id matching `^[a-z0-9_]{2,40}$`.
 * - Latin names: lowercase, non-alphanumerics -> `_`, trimmed/collapsed, truncated to 40.
 * - A result shorter than 2 chars (Hebrew-only input, or a name made entirely of
 *   punctuation) gets a random suffix so it still satisfies the length check.
 * - `existingIds` lets the caller avoid a collision (e.g. "Bench" and "bench!" both
 *   slugifying to "bench") by appending `_2`, `_3`, ... (never exceeding 40 chars).
 */
export function slugifyEquipmentId(nameEn: string, existingIds: Iterable<string> = []): string {
  const base = stripToAsciiWords(nameEn).slice(0, MAX_LEN);
  let candidate = base.length >= MIN_LEN ? base : `${base ? base + '_' : 'eq_'}${randomSuffix()}`.slice(0, MAX_LEN);
  if (candidate.length < MIN_LEN) candidate = `eq_${randomSuffix()}`;

  const taken = new Set(existingIds);
  if (!taken.has(candidate)) return candidate;

  let n = 2;
  while (true) {
    const suffix = `_${n}`;
    const trimmedBase = candidate.slice(0, MAX_LEN - suffix.length);
    const next = `${trimmedBase}${suffix}`;
    if (!taken.has(next)) return next;
    n++;
  }
}
